import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  connect() {
    this.element.querySelectorAll('img').forEach(img => {
      img.style.cursor = 'zoom-in'
      img.addEventListener('click', (e) => this.open(e))
    })
  }

  disconnect() {
    // Tear down a still-open overlay (e.g. on Turbolinks navigation) so we never
    // leak the keydown listener or leave the page scroll-locked.
    this.close()
  }

  open(event) {
    const src = event.currentTarget.querySelector('img')?.src || event.currentTarget.src
    const alt = event.currentTarget.querySelector('img')?.alt || event.currentTarget.alt || ''

    this.scale = 1
    this.originX = 0
    this.originY = 0
    this.isDragging = false

    const overlay = document.createElement('div')
    overlay.id = 'lightbox-overlay'
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 9999;
      background: rgba(0,0,0,0.9);
      -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center;
      cursor: zoom-in; touch-action: none;
      opacity: 0; transition: opacity 0.2s ease;
    `

    const img = document.createElement('img')
    img.src = src
    img.alt = alt
    img.style.cssText = `
      max-width: 95vw; max-height: 90vh;
      object-fit: contain; border-radius: 8px;
      box-shadow: 0 1.5rem 4rem rgba(0,0,0,0.5);
      transform: scale(0.96); transform-origin: center center;
      transition: transform 0.1s ease, opacity 0.25s ease; opacity: 0;
      user-select: none; pointer-events: none;
    `

    const closeBtn = document.createElement('button')
    closeBtn.innerHTML = '&times;'
    closeBtn.setAttribute('aria-label', 'Close')
    closeBtn.style.cssText = `
      position: fixed; top: 18px; right: 20px;
      width: 44px; height: 44px; border-radius: 50%;
      background: rgba(255,255,255,0.12); border: none; color: #fff;
      font-size: 1.75rem; line-height: 1; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      z-index: 10000; transition: background 0.2s ease;
    `
    closeBtn.addEventListener('mouseenter', () => { closeBtn.style.background = 'rgba(255,255,255,0.25)' })
    closeBtn.addEventListener('mouseleave', () => { closeBtn.style.background = 'rgba(255,255,255,0.12)' })

    const caption = document.createElement('div')
    caption.textContent = 'Scroll to zoom · Esc to close'
    caption.style.cssText = `
      position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%);
      color: rgba(255,255,255,0.7); font-size: 0.85rem; letter-spacing: 0.02em;
      background: rgba(0,0,0,0.35); padding: 0.4rem 0.9rem; border-radius: 50rem;
      z-index: 10000; pointer-events: none; transition: opacity 0.3s ease;
    `

    overlay.appendChild(img)
    overlay.appendChild(closeBtn)
    overlay.appendChild(caption)
    document.body.appendChild(overlay)
    document.body.style.overflow = 'hidden'

    this._overlay = overlay
    this._img = img
    this._caption = caption

    const close = () => this.close()
    closeBtn.addEventListener('click', (e) => { e.stopPropagation(); close() })
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close() })
    this._keyHandler = (e) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', this._keyHandler)

    overlay.addEventListener('wheel', (e) => this._onWheel(e), { passive: false })
    overlay.addEventListener('mousedown', (e) => this._onMouseDown(e))
    overlay.addEventListener('mousemove', (e) => this._onMouseMove(e))
    overlay.addEventListener('mouseup', () => this._onMouseUp())

    overlay.addEventListener('touchstart', (e) => this._onTouchStart(e), { passive: false })
    overlay.addEventListener('touchmove', (e) => this._onTouchMove(e), { passive: false })
    overlay.addEventListener('touchend', (e) => this._onTouchEnd(e))

    // Fade/scale in, then dismiss the hint caption on the first interaction.
    this._fadeIn()
    const dismissHint = () => { if (this._caption) this._caption.style.opacity = '0' }
    ;['wheel', 'mousedown', 'touchstart'].forEach((ev) =>
      overlay.addEventListener(ev, dismissHint, { once: true, passive: true })
    )
  }

  close() {
    if (this._overlay) {
      document.removeEventListener('keydown', this._keyHandler)
      this._overlay.remove()
      this._overlay = null
      this._img = null
      this._caption = null
      document.body.style.overflow = ''
    }
  }

  _fadeIn() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce) {
      this._img.style.transition = 'none'
      this._overlay.style.opacity = '1'
      this._img.style.opacity = '1'
      this._applyTransform()
      requestAnimationFrame(() => {
        if (this._img) this._img.style.transition = 'transform 0.1s ease, opacity 0.25s ease'
      })
      return
    }
    requestAnimationFrame(() => {
      if (!this._overlay) return
      this._overlay.style.opacity = '1'
      this._img.style.opacity = '1'
      this._applyTransform() // animates the initial scale(0.96) -> scale(1)
    })
  }

  _applyTransform() {
    this._img.style.transform = `translate(${this.originX}px, ${this.originY}px) scale(${this.scale})`
    this._overlay.style.cursor = this.scale > 1 ? 'grab' : 'zoom-in'
  }

  _onWheel(e) {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.85 : 1.15
    this.scale = Math.min(Math.max(this.scale * delta, 1), 5)
    if (this.scale === 1) { this.originX = 0; this.originY = 0 }
    this._applyTransform()
  }

  _onMouseDown(e) {
    if (this.scale <= 1) return
    this.isDragging = true
    this.lastX = e.clientX
    this.lastY = e.clientY
    this._overlay.style.cursor = 'grabbing'
  }

  _onMouseMove(e) {
    if (!this.isDragging) return
    this.originX += e.clientX - this.lastX
    this.originY += e.clientY - this.lastY
    this.lastX = e.clientX
    this.lastY = e.clientY
    this._img.style.transition = 'none'
    this._applyTransform()
  }

  _onMouseUp() {
    this.isDragging = false
    if (this.scale > 1) this._overlay.style.cursor = 'grab'
  }

  _onTouchStart(e) {
    if (e.touches.length === 2) {
      e.preventDefault()
      this.pinchStartDist = this._pinchDist(e.touches)
      this.pinchStartScale = this.scale
    } else if (e.touches.length === 1 && this.scale > 1) {
      this.isDragging = true
      this.lastX = e.touches[0].clientX
      this.lastY = e.touches[0].clientY
    }
  }

  _onTouchMove(e) {
    if (e.touches.length === 2) {
      e.preventDefault()
      const dist = this._pinchDist(e.touches)
      this.scale = Math.min(Math.max(this.pinchStartScale * (dist / this.pinchStartDist), 1), 5)
      if (this.scale === 1) { this.originX = 0; this.originY = 0 }
      this._img.style.transition = 'none'
      this._applyTransform()
    } else if (e.touches.length === 1 && this.isDragging) {
      e.preventDefault()
      this.originX += e.touches[0].clientX - this.lastX
      this.originY += e.touches[0].clientY - this.lastY
      this.lastX = e.touches[0].clientX
      this.lastY = e.touches[0].clientY
      this._img.style.transition = 'none'
      this._applyTransform()
    }
  }

  _onTouchEnd(e) {
    if (e.touches.length < 2) this.isDragging = false
  }

  _pinchDist(touches) {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }
}
